import { useTranslation } from 'react-i18next';
import FullScreenModal from '../../../ui/primitives/FullScreenModal';
import { BuildModeDemo, TapMenuDemo, DragDemo } from './GestureDemos';

interface RoomOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Onboarding modal for owner — Furniture Management Guide (6 illustrated steps; build mode and the gestures are live demos). */
export default function RoomOnboardingModal({ isOpen, onClose }: RoomOnboardingModalProps) {
  const { t } = useTranslation();

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("Furniture Management Guide")}
      showBackButton
      padding="0 20px 32px 20px"
      background="#0f0f0f"
    >
      {/* Centered + width constrained to avoid ugly stretching on wide screens */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
          maxWidth: 520,
          margin: "0 auto",
          color: "#fff",
          fontSize: "14px",
          lineHeight: "1.5",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              1
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("First turn on build mode with the switch in the top right corner — only then can furniture be moved, flipped and added. It is off every time you enter the house.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <BuildModeDemo />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              2
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("To place furniture, select them from the purchased furniture menu.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <img
              src="/ui/onboarding/step_1_place_from_my_furniture.webp"
              alt="Place furniture"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "12px",
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              3
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("You can also order instant furniture delivery in the shop.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <img
              src="/ui/onboarding/step_2_buy_from_shop.webp"
              alt="Buy from shop"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "12px",
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              4
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("You can also do a preview. Furniture during preview is temporary.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <img
              src="/ui/onboarding/step_3_test_drive.webp"
              alt="Test drive"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "12px",
              }}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              5
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("Tap a piece of furniture to open its settings: flip it, put it back into storage, or cancel a preview.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <TapMenuDemo />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "rgba(55, 191, 164, 0.85)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              6
            </div>
            <p style={{ margin: 0, flex: 1 }}>{t("Drag furniture with your finger to move it — it lifts above your finger so you can see it. The tiles under it show where it will stand; red means the spot is taken.")}</p>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
            <DragDemo />
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
}
